import React from "react";
import { BackUrlContext } from "@/components/ui/organisms/BackUrlContext";
import { Outlet } from "react-router-dom";
import MTopBar from "../ui/atoms/MTopBar";
import MFooterNavigation from "../ui/atoms/MFooterNavigation";

/**
 * Layout: Used for most app screens.
 * Renders a top bar, main content, and persistent footer navigation.
 */
interface LayoutProps {
  children?: React.ReactNode;
  hideBack?: boolean;
  backUrl?: string;
  hideProfilePicker?: boolean;
  showGradient?: boolean;
  hideFooterNav?: boolean;
  hideExit?: boolean;
  onEdit?: () => void;
  showEdit?: boolean;
  onBack?: () => void;
  exitPath?: string;
  skipExitConfirmation?: boolean;
  // Collapse MTopBar's dead padding for pages that render their own full
  // header (back/close button included) — MTopBar still renders empty
  // (hideBack+hideProfilePicker) but keeps ~24px of padding regardless,
  // which shows up as a stray bar above pages whose own header starts with
  // a saturated (non near-white) color. This keeps the safe-area-inset-top
  // reservation (still needed on notched devices) while dropping the rest.
  hideTopBar?: boolean;
}

const Layout: React.FC<LayoutProps> = ({
  children,
  hideBack = false,
  backUrl,
  hideProfilePicker = false,
  showGradient = false,
  hideFooterNav = false,
  hideExit = false,
  onEdit,
  showEdit = false,
  onBack,
  exitPath,
  skipExitConfirmation = false,
  hideTopBar = false,
}) => {
  return (
    <BackUrlContext.Provider value={backUrl}>
      <div
        className="flex flex-col overflow-hidden"
        style={{
          height: "calc(var(--vh, 1vh) * 100)",
          background: showGradient
            ? "linear-gradient(181deg, #FBF6FF 77.96%)"
            : "#fff",
        }}
      >
        <MTopBar
          hideBack={hideBack}
          hideProfilePicker={hideProfilePicker}
          hideExit={hideExit}
          onEdit={onEdit}
          showEdit={showEdit}
          onBack={onBack}
          exitPath={exitPath}
          skipExitConfirmation={skipExitConfirmation}
          minimal={hideTopBar}
        />
        <main
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-white"
          style={{
            // Footer is position:fixed (85px) and overlays content — reserve
            // space for it (plus the bottom safe-area inset) so the last
            // content isn't hidden behind it.
            paddingBottom: hideFooterNav
              ? "env(safe-area-inset-bottom)"
              : "calc(85px + env(safe-area-inset-bottom))",
          }}
        >
          {children ? children : <Outlet />}
        </main>
        {!hideFooterNav && <MFooterNavigation />}
      </div>
    </BackUrlContext.Provider>
  );
};

export default Layout;
